import { AvailableUnit } from "../../types/property-types";
import {
  DispatchFunction,
  FeeTransparencyState,
  FeeStateActions,
  FeeActions,
} from "../../types/fee-state-types";
import { Fee } from "../../types/fee-types";
import { getAllFees, isFeePerApplicant } from "./getFees";
import { getSelectedBaseRent } from "../leases/handleLeasing";

// Set Initial Fee State
/*--------------------------------------------------------------------------------*/
export const initialFeeState = {
  ...({} as FeeTransparencyState),
  numberOfApplicants: 1,
} as FeeTransparencyState;

// Clear State
/*--------------------------------------------------------------------------------*/
export const clearState = (dispatch: DispatchFunction) => () => {
  dispatch({ type: FeeStateActions.CLEAR_STATE, payload: null });
};

// Set Fees
/*--------------------------------------------------------------------------------*/
export const setFees = (dispatch: DispatchFunction) => (feeList?: Fee[]) => {
  if (!feeList) {
    return;
  }
  const allFees = getAllFees(feeList);
  dispatch({
    type: FeeStateActions.SET_ALL_FEES,
    payload: allFees,
  });
};

// Update Selected Fee
/*--------------------------------------------------------------------------------*/
export const updateSelectedFee =
  (dispatch: DispatchFunction, currentState: FeeTransparencyState) =>
  (options: {
    selectedFee: Fee;
    action: FeeActions;
    changeAmount?: number;
  }) => {
    let updatedFees;
    const { selectedFee, action, changeAmount = 1 } = options;
    const { allFees } = currentState;

    switch (action) {
      case FeeActions.INCREMENT:
        updatedFees = allFees.map((fee) => {
          // First, handle the fee itself.
          if (fee.id === selectedFee.id) {
            if (
              isFeePerApplicant(selectedFee) &&
              selectedFee.frequency === "one_time" &&
              !selectedFee.isRequired
            ) {
              // Instead of adding, set activeCount to changeAmount (which for this fee should be number of applicants)

              fee.activeCount = changeAmount;
            } else {
              fee.activeCount += changeAmount;
            }
          }
          // Also apply changes to pet fees if applicable.
          if (selectedFee.category === "pets" && fee.isAdditionalPetFee) {
            fee.activeCount += changeAmount;
          }
          return fee;
        });
        break;
      case FeeActions.DECREMENT:
        updatedFees = allFees.map((fee) => {
          if (fee.id === selectedFee.id) {
            if (
              isFeePerApplicant(selectedFee) &&
              selectedFee.frequency === "one_time" &&
              !selectedFee.isRequired
            ) {
              fee.activeCount = changeAmount;
            } else {
              fee.activeCount = Math.max(0, fee.activeCount - changeAmount);
            }
          }
          if (
            selectedFee.category === "pets" &&
            fee.isAdditionalPetFee &&
            fee.activeCount > 0
          ) {
            fee.activeCount = Math.max(0, fee.activeCount - changeAmount);
          }
          return fee;
        });
        break;
      case FeeActions.RESET:
        updatedFees = allFees.map((fee) => {
          if (fee.id === selectedFee.id) {
            fee.activeCount = 0;
          }
          return fee;
        });
        break;
      default:
        throw new Error("Invalid action");
    }
    dispatch({
      type: FeeStateActions.UPDATE_FEES,
      payload: updatedFees,
    });
  };

// Set Number Of Applicants
/*--------------------------------------------------------------------------------*/
export const setNumberOfApplicants =
  (dispatch: DispatchFunction) => (numberOfApplicants: number) => {
    dispatch({
      type: FeeStateActions.SET_NUMBER_OF_APPLICANTS,
      payload: numberOfApplicants,
    });
  };

// Set Selected Unit
/*--------------------------------------------------------------------------------*/
export const setSelectedUnit =
  (dispatch: DispatchFunction) => (selectedUnit: AvailableUnit) => {
    dispatch({
      type: FeeStateActions.SET_SELECTED_UNIT,
      payload: selectedUnit,
    });
  };

// Set Selected Lease Term
/*--------------------------------------------------------------------------------*/
export const setSelectedLeaseTerm =
  (dispatch: DispatchFunction) => (selectedLeaseTerm: string) => {
    dispatch({
      type: FeeStateActions.SET_SELECTED_LEASE_TERM,
      payload: selectedLeaseTerm,
    });
  };

// Set Selected MoveIn Date
/*--------------------------------------------------------------------------------*/
export const setSelectedMoveInDate =
  (dispatch: DispatchFunction) => (selectedMoveInDate: string) => {
    dispatch({
      type: FeeStateActions.SET_SELECTED_MOVE_IN_DATE,
      payload: selectedMoveInDate,
    });
  };

// Fee Reducer
/*--------------------------------------------------------------------------------*/
export const feeReducer = (
  state: FeeTransparencyState,
  action: any,
): FeeTransparencyState => {
  switch (action.type) {
    case FeeStateActions.SET_SELECTED_UNIT:
      return {
        ...state,
        selectedUnit: action.payload,
      };
    case FeeStateActions.SET_SELECTED_MOVE_IN_DATE: {
      return {
        ...state,
        selectedMoveInDate: action.payload,
        selectedBaseRent: getSelectedBaseRent(
          state.selectedUnit,
          state.selectedLeaseTerm,
          action.payload,
        ),
      };
    }
    case FeeStateActions.SET_SELECTED_LEASE_TERM:
      return {
        ...state,
        selectedLeaseTerm: action.payload,
        selectedBaseRent: getSelectedBaseRent(
          state.selectedUnit,
          action.payload,
          state.selectedMoveInDate,
        ),
      };
    case FeeStateActions.SET_NUMBER_OF_APPLICANTS:
      return {
        ...state,
        numberOfApplicants: action.payload,
      };
    case FeeStateActions.SET_ALL_FEES:
      return {
        ...state,
        allFees: action.payload,
      };
    case FeeStateActions.UPDATE_FEES:
      return {
        ...state,
        allFees: action.payload,
      };
    case FeeStateActions.CLEAR_STATE:
      return initialFeeState;

    default:
      return state;
  }
};
